import type { ComponentType } from 'react';
import {
  SecuritySummaryIcon,
  MonitoringIcon,
  // SecurityToolingIcon,
  // AuthenticationIcon,
  // AuthorizationIcon,
  // SupplierAgreementsIcon,
  InitialQuestionnaireIcon,
  ComplianceIcon,
  OnsiteComplianceAssessmentIcon,
} from './icons';

/**
 * Shared types for the BusinessApplicationDetailsPage module split.
 *
 * These mirror the JSON shapes returned by the KSDP Lambdas invoked via
 * the business-applications-backend plugin (see its router.ts):
 *   - Application: GET /applications/{ban} (ksdp-get-app)
 *   - Report: entries in Application.reports, enriched by ksdp-get-reports'
 *     join against evaluation_request for content_id/requested_by/
 *     security_summary_version (see DB_schema.md section 3).
 *   - MonitoringRecord: GET/POST /monitoring/{ban} (ksdp-get-monitoring /
 *     ksdp-upsert-monitoring) — a distinct feature, own `monitoring` schema.
 *
 * All fields are optional/nullable where the backend can legitimately omit
 * or null them (e.g. a report with a REQUESTED evaluation that hasn't
 * produced scores yet).
 */

export interface Report {
  request_id?: string | null;
  timestamp: string;
  s3_key?: string | null;
  url?: string | null;
  report_url?: string | null;
  system_risk_score?: number | null;
  completeness_score?: number | null;
  content_id?: number | null;
  requested_by?: string | null;
  security_summary_version?: number | null;
  validation_issues_count?: number | null;
  cross_source_gaps_count?: number | null;
  traceability_gaps_count?: number | null;
  lifecycle_gaps_count?: number | null;
  recommendations?: string[] | null;
}

export interface Application {
  number: string;
  name: string;
  lifecycle?: string | null;
  application_type?: string | null;
  security_impact?: number | string | null;
  business_unit?: string | null;
  department?: string | null;
  owner?: string | null;
  /** The CMDB `url` field exactly as stored: free text that may hold
   * placeholders ("N/A"), bare domains, or several addresses. Parse it with
   * `parseApplicationUrl` before showing it. */
  application_url?: string | null;
  cyber_security_questionnaire?: string | null;
  cyber_security_questionnaire_id?: string | null;
  latest_security_summary_link?: string | null;
  latest_security_summary_version?: number | null;
  significant_changes?: boolean | null;
  latest_security_summary_evaluated?: boolean | null;
  /** The in-flight (non-FAILED) evaluation_request's id for the current
   * security summary version, if one exists — lets the page resume status
   * polling after a refresh instead of only being able to poll right after
   * a fresh "Request Evaluation" click. Null once there is nothing in
   * flight (no request yet, or the last one reached COMPLETED/FAILED and a
   * new one hasn't been requested). */
  latest_evaluation_request_id?: string | null;
  /** Status of latest_evaluation_request_id (REQUESTED/IN_PROGRESS/COMPLETED),
   * or null when latest_evaluation_request_id is null. See get-app's
   * _get_latest_evaluation_request(). */
  latest_evaluation_status?: string | null;
  /** Application compliance links - which applications this BAN is linked
   * to for compliance status synchronization. From ksdp-get-app response. */
  complianceLinks?: {
    linkGroupId: string;
    members: { ban: string; name: string }[];
  } | null;
  reports?: Report[];
}

export interface MonitoringRecord {
  ban: string;
  onboarding_status: string | null;
  onboarding_date: string | null;
  validation_email_sent: boolean;
  validation_email_sent_at: string | null;
  owner_response: string | null;
  security_review_requested: boolean;
  security_review_requested_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

/** Identifies which module's detail view (or the dashboard grid) is shown. */
export type DetailViewId =
  | 'dashboard'
  | 'security-summary'
  | 'monitoring'
  | 'security-tooling'
  | 'authentication'
  | 'authorization'
  | 'supplier-agreements'
  | 'initial-questionnaire'
  | 'compliance'
  | 'onsite-compliance-assessment';

export interface DashboardCardDef {
  id: DetailViewId;
  label: string;
  /** SVG icon component (see ./icons/) — rendered with `currentColor` fill, no props. */
  Icon: ComponentType;
}

/**
 * The 8 module entry points shown on the "Application Modules" dashboard
 * grid. `id` doubles as the DetailViewId used to open that module.
 */
export const DASHBOARD_CARDS: DashboardCardDef[] = [
  {
    id: 'security-summary',
    label: 'Security Summary',
    Icon: SecuritySummaryIcon,
  },
  { id: 'monitoring', label: 'Monitoring', Icon: MonitoringIcon },
  /*
  {
    id: 'security-tooling',
    label: 'Security Tooling',
    Icon: SecurityToolingIcon,
  },
  { id: 'authentication', label: 'Authentication', Icon: AuthenticationIcon },
  { id: 'authorization', label: 'Authorization', Icon: AuthorizationIcon },
  {
    id: 'supplier-agreements',
    label: 'Supplier Agreements',
    Icon: SupplierAgreementsIcon,
  },
  */
  {
    id: 'initial-questionnaire',
    label: 'Initial Questionnaire',
    Icon: InitialQuestionnaireIcon,
  },
  { id: 'compliance', label: 'Compliance', Icon: ComplianceIcon },
  {
    id: 'onsite-compliance-assessment',
    label: 'Onsite Compliance Assessment',
    Icon: OnsiteComplianceAssessmentIcon,
  },
];

export type ComplianceRequirementStatus =
  | 'not_started'
  | 'in_progress'
  | 'implemented'
  | 'not_applicable';

export type ComplianceHistoryChangeType =
  | 'baseline'
  | 'submission'
  | 'exception_opened'
  | 'exception_message'
  | 'exception_status';

export interface ApplicationComplianceHistoryEntry {
  historyId: string;
  status: ComplianceRequirementStatus;
  supportingLinks?: string | null;
  evidenceDescription?: string | null;
  naJustification?: string | null;
  updatedBy?: string | null;
  recordedAt: string;
  changeType: ComplianceHistoryChangeType;
  exceptionCaseId?: string | null;
  exceptionDetail?: string | null;
  actorRole?: string | null;
}

export interface ApplicationComplianceAutomation {
  managed: boolean;
  source: string;
  available: boolean;
  message?: string | null;
}

export interface ApplicationComplianceExceptionCase {
  caseId: string;
  status: 'open' | 'rejected';
  messageCount: number;
  daysRemaining?: number | null;
}

// Mirrors backend serialize_case(): messages carry no messageId.
export interface ApplicationComplianceExceptionMessage {
  body: string;
  author: string;
  authorRole?: 'requester' | 'admin';
  postedAt: string;
}

export interface ApplicationCountermeasure {
  ref: string;
  name: string;
  text: string;
  links: { url: string; text: string }[];
  standardRefs: string[];
  requirementId: string;
  usecaseRef?: string | null;
  threatRef?: string | null;
  status: ComplianceRequirementStatus;
  supportingLinks?: string | null;
  evidenceDescription?: string | null;
  naJustification?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  history?: ApplicationComplianceHistoryEntry[];
  automation?: ApplicationComplianceAutomation | null;
  exceptionCase?: ApplicationComplianceExceptionCase | null;
  linkedRequirements?: Array<{
    requirementId: string;
    standardId: string;
    standardName: string;
    ref: string;
    name: string;
  }>;
}

export interface ApplicationComplianceThreat {
  ref: string;
  name: string;
  text: string;
  countermeasureRefs: { ref: string; mitigation: string }[];
}

export interface ApplicationComplianceUsecase {
  ref: string;
  name: string;
  text: string;
  threats: ApplicationComplianceThreat[];
}

/**
 * The Baseline Requirements section. Applicability is driven exclusively by
 * the app-type/impact-level matrix (automatic) — never by manual
 * assignment. `countermeasures`/`usecases` are empty when not applicable.
 */
export interface ApplicationComplianceBaseline {
  applicable: boolean;
  reason?: 'unmapped_impact_level' | 'unmapped_app_type' | null;
  appType?: string | null;
  impactLevelId?: string | null;
  countermeasures: ApplicationCountermeasure[];
  usecases: ApplicationComplianceUsecase[];
}

/**
 * One Standard manually assigned to this BAN via
 * compliance.standard_application_assignments (e.g. ITGC, EU NIS2, KONE
 * CRA). Always fully applicable once assigned — there is no matrix to
 * filter against, unlike Baseline. Kept as its own section rather than
 * merged into Baseline's countermeasures/usecases so each Standard's
 * applicability mechanism (automatic vs. manual, and any future
 * standard-specific automation) stays independent and unambiguous.
 */
export interface ApplicationComplianceAssignedStandard {
  standardId: string;
  standardSlug: string;
  standardName: string;
  countermeasures: ApplicationCountermeasure[];
  usecases: ApplicationComplianceUsecase[];
}

export interface ApplicationComplianceResponse {
  ban: string;
  baseline: ApplicationComplianceBaseline;
  assignedStandards: ApplicationComplianceAssignedStandard[];
}
